import { Helmet } from "react-helmet-async";
import { SITE_URL, SITE_NAME, ALLOW_INDEXING, OG_IMAGE_PATH, OG_IMAGE_ALT, normalizePath } from "@/lib/seo";
import routeMeta from "@/seo/routeMeta.json";

/**
 * Per-route <title>, description, canonical, robots, Open Graph and Twitter meta (blueprint D8, section 12;
 * SEO spec 3.2). `path` is the site-relative path used for the canonical URL. `noindex` forces noindex
 * regardless of the launch flag (admin, waitlist/coming-soon pages, 404s); a route marked noindex in
 * src/seo/routeMeta.json is forced noindex too, so the sitemap and the page can never disagree.
 *
 * A noindex page emits NO canonical: a canonical on a noindex or 404 page (previously "/" by default)
 * sends contradictory signals.
 */
export default function Seo({ title, description, path = "/", noindex = false, exactTitle = null }) {
  const normalized = normalizePath(path);
  // exactTitle: a complete, CLO-approved <title> that must not get the " | RateCurrent" suffix appended (the About page).
  const fullTitle = exactTitle || (title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Texas ESID Lookup & Electric Bill Explainer`);
  const forcedNoindex = noindex || Boolean(routeMeta[normalized] && routeMeta[normalized].noindex);
  const robots = ALLOW_INDEXING && !forcedNoindex ? "index,follow" : "noindex,nofollow";
  const canonical = `${SITE_URL}${normalized}`;
  const image = OG_IMAGE_PATH ? `${SITE_URL}${OG_IMAGE_PATH}` : null;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={robots} />
      {!forcedNoindex && <link rel="canonical" href={canonical} />}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      {!forcedNoindex && <meta property="og:url" content={canonical} />}
      <meta property="og:type" content="website" />
      {image && <meta property="og:image" content={image} />}
      {image && <meta property="og:image:alt" content={OG_IMAGE_ALT} />}
      <meta name="twitter:card" content={image ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {image && <meta name="twitter:image" content={image} />}
    </Helmet>
  );
}
